import { createSimpleStore } from "@expert/state-utils";

import type { C360HistoryInteraction } from "../api/interactions";

/** `searching`, then `linked` (a subscription is on the line), `no_subscriptions`, or `failed` (the lookup didn't complete). */
export type C360CustomerStatus = "searching" | "linked" | "no_subscriptions" | "failed";

export interface C360CustomerSession {
    status: C360CustomerStatus;
    /** The 10-digit number looked up; a confirm with another number looks up again. */
    searchedMdn: string;
    /** The name sent with that lookup, which went on the line. */
    searchedName?: string;
    /** The name the expert confirmed for whoever is on the call. */
    customerName?: string;
    /**
     * Whether the lookup asked C360 for history (`isC360CustomerHistoryEnabled`); the panel shows only then, so it and
     * the request can't disagree. Unset until the first lookup has read the flag.
     */
    historyRequested?: boolean;
    /** The customer's latest interactions across their subscriptions, from the last lookup that linked. */
    history?: C360CustomerHistory;
}

export interface C360CustomerHistory {
    subscriptionCount: number;
    /** False when some history is missing: either a subscription's history fetch failed, or C360 cut the customer search short and more subscriptions may exist. */
    historyComplete: boolean;
    interactions: C360HistoryInteraction[];
}

interface C360CustomerState {
    sessions: Record<string, C360CustomerSession>;
}

interface C360CustomerActions {
    getSession: (sessionId: string) => C360CustomerSession | undefined;
    setSession: (sessionId: string, session: C360CustomerSession) => void;
    updateSession: (sessionId: string, update: Partial<C360CustomerSession>) => void;
    removeSession: (sessionId: string) => void;
}

type C360CustomerStore = C360CustomerState & C360CustomerActions;

/** Per session: what was looked up in C360, so a confirm can tell whether to look up again. */
export const useC360CustomerStore = createSimpleStore<C360CustomerStore>({
    devtools: {
        store: "c360Customer",
        name: "workspace/c360-customer",
        isProd: import.meta.env.VITE_ENV_TYPE === "production",
    },
})((set, get) => ({
    sessions: {},
    getSession: (sessionId) => get().sessions[sessionId],
    setSession: (sessionId, session) =>
        set((state) => ({ sessions: { ...state.sessions, [sessionId]: session } }), false, "setSession"),
    updateSession: (sessionId, update) =>
        set(
            (state) => {
                const current = state.sessions[sessionId];
                return current ? { sessions: { ...state.sessions, [sessionId]: { ...current, ...update } } } : state;
            },
            false,
            "updateSession",
        ),
    removeSession: (sessionId) =>
        set(
            (state) => {
                const sessions = { ...state.sessions };
                delete sessions[sessionId];
                return { sessions };
            },
            false,
            "removeSession",
        ),
}));
